import React, { useRef, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Navigate, Link } from "react-router-dom";
import { Activity, Download, Trash2, X, BarChart3, RefreshCw, Loader2, Users, Flag, PawPrint, Droplets, Mail, Send, Bell, MapPin, Beaker, FileUp, ShieldAlert, Database, ShieldCheck, Bot, Ban, Lightbulb, Check, ThumbsUp, Archive, XCircle, Eye, Trees, Zap, Wind, Bus, ArrowRight, Utensils, Target } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { track } from "../lib/telemetry";
import { MUD_LEVELS, levelMeta } from "../components/MudBadge";
import EventDetailsModal from "../components/EventDetailsModal";
import ActivityStream from "../components/ActivityStream";
import AdminRegionHeatMap from "../components/AdminRegionHeatMap";
import HideReviewDialog, { HIDE_REASONS } from "../components/HideReviewDialog";

function isAdmin(user) {
  return !!(user && user.is_admin);
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-GB"); } catch { return iso; }
}

function SummaryCards({ summary, totalEvents, activeUsers }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="telemetry-summary">
      <div className="td-card p-4">
        <div className="td-section-label">Total events</div>
        <div className="font-heading text-3xl text-brand-text mt-1">{totalEvents.toLocaleString("en-GB")}</div>
      </div>
      <div className="td-card p-4">
        <div className="td-section-label">Active users</div>
        <div className="font-heading text-3xl text-brand-text mt-1">{activeUsers.toLocaleString("en-GB")}</div>
      </div>
      <div className="td-card p-4 col-span-2">
        <div className="td-section-label">Top event types</div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {summary.slice(0, 6).map((s) => (
            <span key={s.event_type} className="td-chip">
              {prettyEvent(s.event_type)} · {s.count}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Heatmap({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="td-card p-5 sm:p-6" data-testid="telemetry-heatmap">
      <div className="flex items-center gap-2">
        <BarChart3 size={16} className="text-brand-primary" />
        <h2 className="font-heading text-lg font-medium">Top regions — search detail</h2>
        <span className="text-xs text-brand-subtle ml-auto">last 90 days · map searches only</span>
      </div>
      {data.length === 0 ? (
        <p className="text-sm text-brand-subtle mt-3">No map searches recorded yet.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {data.map((row) => {
            const pct = Math.round((row.count / max) * 100);
            return (
              <div key={row.region} className="flex items-center gap-3 text-sm">
                <span className="w-32 sm:w-44 truncate text-brand-text" title={row.region}>{row.region}</span>
                <div className="flex-1 h-5 rounded-full bg-brand-sand overflow-hidden border border-brand-border/60">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-brand-primary to-brand-accent transition-all"
                    style={{ width: `${pct}%` }}
                    title={`${row.count} searches`}
                  />
                </div>
                <span className="w-12 text-right text-brand-subtle">{row.count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const EVENT_LABELS = {
  discover_view: "Browsed Discover",
  location_view: "Viewed Trail Details",
  map_view: "Opened Map",
  map_search: "Performed Map Search",
  search_location: "Searched Location",
  share_trail: "Shared a Trail",
  install_prompt_shown: "Install Prompt Shown",
  install_accepted: "User Installed PWA",
  install_outcome_dismissed: "User Cancelled Install Prompt",
  install_dismissed: "User Tapped 'Maybe Later'",
  install_howto_opened: "iOS Install Guide Opened",
  install_completed: "Install Completed",
  install_test_event: "Admin Test Event",
};

function prettyEvent(key) {
  if (EVENT_LABELS[key]) return EVENT_LABELS[key];
  // Auto-prettify unknown event_types: "some_event_name" → "Some Event Name"
  return (key || "—")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettyKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettyValue(v) {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString("en-GB");
  if (typeof v === "string") {
    // Soft-truncate long IDs
    if (v.startsWith("loc_") || v.length > 32) return v.slice(0, 14) + "…";
    return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return String(v);
}

function EventData({ row }) {
  // If we have a trail name (server-joined), surface it as the primary label.
  if (row.trail_name) {
    return (
      <Link
        to={`/location/${row.event_data?.trail_id || row.event_data?.location_id}`}
        className="text-brand-primary font-medium hover:underline"
      >
        Trail: {row.trail_name}
      </Link>
    );
  }
  const data = row.event_data || {};
  const entries = Object.entries(data).filter(([k]) => !["trail_id", "location_id"].includes(k));
  if (entries.length === 0) return <span className="text-brand-subtle text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
      {entries.slice(0, 4).map(([k, v]) => (
        <span key={k} className="text-brand-subtle">
          <span className="text-brand-text/70">{prettyKey(k)}:</span>{" "}
          <span className="text-brand-text">{prettyValue(v)}</span>
        </span>
      ))}
    </div>
  );
}

function relativeTime(iso) {
  try {
    const ts = new Date(iso).getTime();
    const diff = Date.now() - ts;
    if (diff < 0) return "just now";
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
}

function EventsTable({ rows, onBlockIp, onViewEvent }) {
  return (
    <div className="td-card p-0 overflow-hidden" data-testid="telemetry-table">
      <div className="px-5 sm:px-6 pt-5 pb-3 flex items-center justify-between">
        <h2 className="font-heading text-lg font-medium">Recent events</h2>
        <span className="text-xs text-brand-subtle">latest {rows.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-sand text-brand-subtle uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-5 py-2">Event</th>
              <th className="text-left px-3 py-2 hidden sm:table-cell">Region</th>
              <th className="text-left px-3 py-2 hidden md:table-cell">Location</th>
              <th className="text-left px-3 py-2">Data</th>
              <th className="text-right px-5 py-2">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const geo = [r.city, r.country].filter(Boolean).join(", ");
              return (
                <tr
                  key={r.event_id}
                  className={`border-t border-brand-border/40 ${r.is_bot ? "bg-brand-error/5" : ""}`}
                  data-testid={`event-row-${r.event_id}`}
                >
                  <td className="px-5 py-2.5 font-medium text-brand-text whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {r.is_bot && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-brand-error/15 text-brand-error border border-brand-error/30"
                          title={`Flagged: ${r.bot_reason || "bot"}`}
                          data-testid={`bot-badge-${r.event_id}`}
                        >
                          <Bot size={9} /> BOT
                        </span>
                      )}
                      {prettyEvent(r.event_type)}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-brand-subtle hidden sm:table-cell">{r.region || "—"}</td>
                  <td className="px-3 py-2.5 text-brand-subtle hidden md:table-cell whitespace-nowrap" title={r.ip || ""}>
                    {geo || <span className="text-brand-subtle/60">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <EventData row={r} />
                      <button
                        data-testid={`view-event-${r.event_id}`}
                        onClick={() => onViewEvent(r.event_id)}
                        className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-sand text-brand-subtle border border-brand-border hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-colors whitespace-nowrap"
                        title="Open event details"
                      >
                        <Eye size={10} /> View
                      </button>
                      {r.is_bot && r.ip && (
                        <button
                          data-testid={`block-ip-${r.event_id}`}
                          onClick={() => onBlockIp(r.ip, r.bot_reason)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-error/10 text-brand-error border border-brand-error/30 hover:bg-brand-error hover:text-white transition-colors whitespace-nowrap"
                          title={`Block ${r.ip}`}
                        >
                          <Ban size={10} /> Block
                        </button>
                      )}
                    </div>
                  </td>
                  <td
                    className="px-5 py-2.5 text-brand-subtle whitespace-nowrap text-right"
                    title={new Date(r.timestamp).toLocaleString("en-GB")}
                  >
                    {relativeTime(r.timestamp)}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-6 text-center text-brand-subtle">No events captured yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrafficHealthCard({ health }) {
  if (!health) return null;
  const { total, bots, humans, bot_pct, window_hours } = health;
  const humanPct = total ? Math.max(0, 100 - bot_pct) : 0;
  const status = bot_pct >= 40 ? "high" : bot_pct >= 15 ? "elevated" : "healthy";
  const statusStyle = {
    healthy: "bg-brand-primary/10 text-brand-primary border-brand-primary/30",
    elevated: "bg-brand-accent/15 text-brand-accent border-brand-accent/30",
    high: "bg-brand-error/15 text-brand-error border-brand-error/30",
  }[status];
  const statusLabel = { healthy: "Healthy", elevated: "Elevated", high: "High bot load" }[status];

  return (
    <div className="td-card p-5 sm:p-6" data-testid="traffic-health-card">
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck size={16} className="text-brand-primary" />
        <h2 className="font-heading text-lg font-medium">Traffic health</h2>
        <span className="text-xs text-brand-subtle">last {window_hours}h</span>
        <span className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border ${statusStyle}`} data-testid="traffic-health-status">
          {statusLabel}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4">
        <div>
          <div className="td-section-label">Total hits</div>
          <div className="font-heading text-2xl text-brand-text mt-0.5">{total.toLocaleString("en-GB")}</div>
        </div>
        <div>
          <div className="td-section-label flex items-center gap-1"><Users size={11} /> Humans</div>
          <div className="font-heading text-2xl text-brand-primary mt-0.5" data-testid="traffic-humans">{humans.toLocaleString("en-GB")}</div>
        </div>
        <div>
          <div className="td-section-label flex items-center gap-1"><Bot size={11} /> Bots</div>
          <div className="font-heading text-2xl text-brand-error mt-0.5" data-testid="traffic-bots">{bots.toLocaleString("en-GB")}</div>
        </div>
      </div>

      <div className="mt-4 flex h-3 rounded-full overflow-hidden border border-brand-border/60 bg-brand-sand" title={`${humanPct.toFixed(1)}% humans · ${bot_pct}% bots`}>
        <div className="bg-brand-primary transition-all" style={{ width: `${humanPct}%` }} />
        <div className="bg-brand-error transition-all" style={{ width: `${bot_pct}%` }} />
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-brand-subtle">
        <span>{humanPct.toFixed(1)}% real users</span>
        <span>{bot_pct}% flagged as bots</span>
      </div>
    </div>
  );
}

const TRAFFIC_FILTERS = [
  { value: "all", label: "All traffic", icon: Activity },
  { value: "humans", label: "Real users only", icon: Users },
  { value: "bots", label: "Flagged bots only", icon: Bot },
];

function FreshLivestockPanel() {
  const q = useQuery({
    queryKey: ["admin-fresh-livestock"],
    queryFn: async () => (await api.get("/admin/community/fresh-livestock", { params: { days: 7 } })).data,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const fmtAgo = (iso) => {
    try {
      const diff = Date.now() - new Date(iso).getTime();
      const hours = Math.floor(diff / 36e5);
      if (hours < 1) return "<1h ago";
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    } catch {
      return "";
    }
  };

  const sevStyle = {
    avoid: "bg-brand-error/15 text-brand-error border-brand-error/30",
    warn: "bg-brand-accent/15 text-brand-accent border-brand-accent/30",
    info: "bg-brand-sand text-brand-subtle border-brand-border",
    calm: "bg-brand-primary/10 text-brand-primary border-brand-primary/30",
  };

  return (
    <div className="td-card p-5 sm:p-6" data-testid="fresh-livestock-panel">
      <div className="flex items-center gap-2">
        <Bell size={16} className="text-brand-accent" />
        <h2 className="font-heading text-lg font-medium">Fresh livestock alerts on saved walks</h2>
        <span className="text-xs text-brand-subtle ml-auto">
          past {q.data?.window_days ?? 7} days
          {q.data && ` · ${q.data.trails_with_alerts ?? 0}/${q.data.saved_trail_count ?? 0} saved trails affected`}
        </span>
      </div>

      <p className="text-xs text-brand-subtle mt-1">
        Trails users have saved to their diary that have had a livestock report in the last week — ideal candidates
        for a manual safety nudge or a content review.
      </p>

      {q.isLoading && (
        <div className="text-sm text-brand-subtle mt-3 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}

      {q.data && q.data.items.length === 0 && (
        <p className="text-sm text-brand-subtle mt-3">
          All quiet — no fresh livestock reports on any saved trails this week.
        </p>
      )}

      {q.data && q.data.items.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="fresh-livestock-list">
          {q.data.items.map((t) => (
            <Link
              key={t.location_id}
              to={`/location/${t.location_id}`}
              data-testid={`fresh-livestock-${t.location_id}`}
              className="flex items-start justify-between gap-3 py-2 px-3 rounded-xl hover:bg-brand-sand transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-brand-text truncate">{t.name}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${sevStyle[t.severity] || sevStyle.info}`}>
                    {t.severity}
                  </span>
                </div>
                <div className="text-xs text-brand-subtle mt-0.5 flex items-center gap-2 flex-wrap">
                  <MapPin size={11} /> {t.region || "United Kingdom"}
                  <span>· {t.types.join(", ")}</span>
                  <span>· {t.report_count} report{t.report_count === 1 ? "" : "s"}</span>
                  <span>· saved by {t.saved_by_count}</span>
                </div>
                {t.latest_notes && (
                  <div className="text-xs text-brand-text/80 mt-1 italic truncate">&quot;{t.latest_notes}&quot;</div>
                )}
              </div>
              <div className="text-xs text-brand-subtle whitespace-nowrap shrink-0">{fmtAgo(t.last_report_at)}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
function ScoutingPipelineTile() {
  const q = useQuery({
    queryKey: ["admin-scout-pipeline"],
    queryFn: async () => (await api.get("/admin/scout-pipeline")).data,
    refetchOnWindowFocus: false,
  });
  const items = q.data?.items || [];

  return (
    <div className="td-card p-5 sm:p-6" data-testid="scouting-pipeline-tile">
      <div className="flex items-center gap-2">
        <Target size={16} className="text-brand-accent" />
        <h2 className="font-heading text-lg font-medium">Scouting Pipeline</h2>
        <span className="ml-auto text-xs text-brand-subtle" data-testid="scout-pipeline-count">
          {items.length} in progress
        </span>
      </div>
      <p className="text-xs text-brand-subtle mt-1">
        Trails part-way to crowd verification — one or two votes short of the 3-scout resolve threshold.
      </p>
      {q.isLoading ? (
        <div className="mt-4 text-sm text-brand-subtle flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-4 text-sm text-brand-subtle" data-testid="scout-pipeline-empty">
          Nothing in the pipeline — no trails currently have partial scout votes.
        </div>
      ) : (
        <ul className="mt-4 space-y-1.5" data-testid="scout-pipeline-list">
          {items.map((it) => (
            <li
              key={it.location_id}
              data-testid={`scout-pipeline-row-${it.location_id}`}
              className="flex items-center justify-between gap-3 text-sm text-brand-text border-b border-brand-border/40 last:border-0 pb-1.5 last:pb-0"
            >
              <Link to={`/location/${it.location_id}`} className="truncate hover:underline">
                {it.name}
              </Link>
              <span className="shrink-0 text-xs font-semibold text-brand-accent">{it.count}/{it.threshold} scouts</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BasecampSignalsTile() {
  const q = useQuery({
    queryKey: ["admin-basecamp-signals"],
    queryFn: async () => (await api.get("/admin/basecamp-signals")).data,
    refetchOnWindowFocus: false,
  });

  const d = q.data;
  const total = d?.total_opens ?? 0;
  const chill = d?.chill ?? 0;
  const high = d?.high_energy ?? 0;
  const energyKnown = chill + high;
  const chillPct = energyKnown ? Math.round((chill / energyKnown) * 100) : 0;
  const highPct = energyKnown ? 100 - chillPct : 0;
  const medianM = d?.median_bus_distance_m;
  const medianKm = typeof medianM === "number" ? (medianM / 1000).toFixed(medianM < 1000 ? 2 : 1) : null;
  const conversionPct = d?.conversion_pct;
  const transitPlans = d?.transit_plans ?? 0;
  const uniqueOpens = d?.unique_opens ?? 0;
  const lunchIntents = d?.lunch_intents ?? 0;
  // "Attach rate" = share of daily basecamp browsers who explicitly signal
  // they want food. Uses `unique_opens` (daily-unique walker×basecamp) as the
  // denominator so both sides are apples-to-apples.
  const lunchAttachPct = uniqueOpens > 0 ? Math.round((lunchIntents / uniqueOpens) * 100) : 0;
  // Only show the pill once we have enough opens to make the number meaningful —
  // a "100% conversion" from a single walker is noise, not signal.
  const showConversion = uniqueOpens >= 5;
  // Colour codes intent — green means walkers are converting, amber means
  // they're browsing but not committing to travel.
  const convClass = conversionPct >= 25
    ? "bg-brand-primary/15 text-brand-primary border-brand-primary/30"
    : conversionPct >= 10
      ? "bg-brand-accent/15 text-brand-accent border-brand-accent/30"
      : "bg-brand-sand text-brand-subtle border-brand-border";

  return (
    <div className="td-card p-5 sm:p-6" data-testid="basecamp-signals-tile">
      <div className="flex items-center gap-2 flex-wrap">
        <Trees size={16} className="text-brand-primary" />
        <h2 className="font-heading text-lg font-medium">Basecamp signals</h2>
        <span className="ml-auto text-xs text-brand-subtle" data-testid="signal-total-opens">
          {total} open{total === 1 ? "" : "s"}
        </span>
        {showConversion && (
          <span
            data-testid="signal-conversion-pill"
            title={`${transitPlans} of ${uniqueOpens} unique walkers who opened a basecamp went on to plan transit to it.`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${convClass}`}
          >
            <ArrowRight size={10} /> {conversionPct}% transit conversion
          </span>
        )}
      </div>

      {q.isLoading && (
        <div className="text-sm text-brand-subtle mt-3 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}

      {d && total === 0 && (
        <p className="text-sm text-brand-subtle mt-3">
          No basecamp opens yet — as walkers browse, this tile will show which curation traits pull them in.
        </p>
      )}

      {d && total > 0 && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="basecamp-signals-body">
          {/* Energy split */}          <div>
            <div className="td-section-label flex items-center gap-1 mb-1.5">
              <Zap size={11} /> Energy match
            </div>
            {energyKnown === 0 ? (
              <div className="text-xs text-brand-subtle">No energy data yet</div>
            ) : (
              <>
                <div className="flex h-3 rounded-full overflow-hidden border border-brand-border/60 bg-brand-sand" title={`${chillPct}% chill · ${highPct}% high energy`}>
                  <div className="bg-brand-primary transition-all" style={{ width: `${chillPct}%` }} />
                  <div className="bg-brand-error transition-all" style={{ width: `${highPct}%` }} />
                </div>
                <div className="mt-1.5 flex justify-between text-[11px]">
                  <span className="text-brand-primary" data-testid="signal-chill-pct">
                    <Wind size={9} className="inline -mt-0.5" /> {chillPct}% chill ({chill})
                  </span>
                  <span className="text-brand-error" data-testid="signal-high-pct">
                    {highPct}% high ({high}) <Zap size={9} className="inline -mt-0.5" />
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Median bus distance */}
          <div>
            <div className="td-section-label flex items-center gap-1 mb-1.5">
              <Bus size={11} /> Median bus distance
            </div>
            {medianKm ? (
              <>
                <div className="font-heading text-2xl text-brand-text" data-testid="signal-median-km">
                  {medianKm} km
                </div>
                <div className="text-[11px] text-brand-subtle">
                  across {d.with_bus_data} open{d.with_bus_data === 1 ? "" : "s"} with transit data
                </div>
              </>
            ) : (
              <div className="text-xs text-brand-subtle">No transit data yet</div>
            )}
          </div>

          {/* Lunch intent — how many daily browsers signalled "I want food". */}
          <div>
            <div className="td-section-label flex items-center gap-1 mb-1.5">
              <Utensils size={11} /> Lunch intent
            </div>
            {lunchIntents > 0 ? (
              <>
                <div className="font-heading text-2xl text-brand-text" data-testid="signal-lunch-intents">
                  {lunchIntents}
                </div>
                <div className="text-[11px] text-brand-subtle">
                  daily activations
                  {uniqueOpens > 0 && (
                    <> · <span data-testid="signal-lunch-attach">{lunchAttachPct}%</span> of browsers</>
                  )}
                </div>
              </>
            ) : (
              <div className="text-xs text-brand-subtle" data-testid="signal-lunch-empty">
                No lunch-filter taps yet — walkers haven&apos;t signalled food intent.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Per-basecamp lunch-intent breakdown — which specific basecamps did
          lunch-hungry walkers convert on? Only rendered once at least one
          basecamp has a real via_lunch_filter open, so the tile stays quiet
          when there's no signal to show. */}
      {d && (d.lunch_breakdown?.length ?? 0) > 0 && (
        <div className="mt-4 pt-4 border-t border-brand-border/50" data-testid="lunch-breakdown">
          <div className="td-section-label flex items-center gap-1 mb-2">
            <Utensils size={11} /> Top basecamps for lunch-intent walkers
          </div>
          <div className="space-y-1.5">
            {d.lunch_breakdown.map((row) => {
              const maxOpens = d.lunch_breakdown[0].unique_opens || 1;
              const pct = Math.round((row.unique_opens / maxOpens) * 100);
              return (
                <Link
                  key={row.basecamp_id}
                  to={`/basecamp/${row.basecamp_id}`}
                  data-testid={`lunch-breakdown-${row.basecamp_id}`}
                  className="flex items-center gap-3 py-1.5 px-2 rounded-xl hover:bg-brand-sand transition-colors group"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-brand-text truncate group-hover:text-brand-primary">
                      {row.name}
                    </div>
                    {row.region && (
                      <div className="text-[10px] text-brand-subtle truncate">
                        <MapPin size={9} className="inline -mt-0.5" /> {row.region}
                      </div>
                    )}
                  </div>
                  <div className="w-24 sm:w-32 h-2 rounded-full bg-brand-sand overflow-hidden border border-brand-border/60 shrink-0">
                    <div
                      className="h-full bg-brand-accent transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold text-brand-accent w-8 text-right shrink-0" data-testid={`lunch-breakdown-count-${row.basecamp_id}`}>
                    {row.unique_opens}
                  </span>
                </Link>
              );
            })}
          </div>
          <p className="text-[10px] text-brand-subtle mt-2 italic">
            Unique walkers who opened this basecamp with the lunch-stop filter active.
          </p>
        </div>
      )}
    </div>
  );
}

function SuggestionsPanel() {
  const qc = useQueryClient();
  const [includeHidden, setIncludeHidden] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const q = useQuery({
    queryKey: ["admin-suggestions", includeHidden],
    queryFn: async () =>
      (await api.get("/admin/suggestions", { params: { include_hidden: includeHidden } })).data,
    refetchOnWindowFocus: false,
  });

  const patch = useMutation({
    mutationFn: async ({ id, status, rejection_reason }) => {
      const body = { status };
      if (rejection_reason !== undefined) body.rejection_reason = rejection_reason;
      return (await api.patch(`/admin/suggestions/${id}`, body)).data;
    },
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-suggestions"] });
      if (vars.status === "accepted") {
        toast.success(
          data.email_sent
            ? "Idea accepted — walker notified by email."
            : "Idea accepted. (No email sent — no contact on file.)"
        );
      } else if (vars.status === "archived") {
        toast("Archived. Toggle 'Show archived' to see it again.");
      } else if (vars.status === "rejected") {
        toast("Marked as rejected.");
        setRejectingId(null);
        setRejectReason("");
      } else if (vars.status === "read") {
        toast("Marked as read.");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed."),
  });

  const onAccept = (row) => {
    const hasEmail = row.user_contact && row.user_contact.includes("@");
    const msg = hasEmail
      ? `Accept this idea? An email will be sent to ${row.user_contact}. This can't be undone.`
      : `Accept this idea? (No email will be sent — no contact was provided.)`;
    if (window.confirm(msg)) patch.mutate({ id: row.suggestion_id, status: "accepted" });
  };

  const startReject = (row) => {
    setRejectingId(row.suggestion_id);
    setRejectReason(row.rejection_reason || "");
  };
  const cancelReject = () => { setRejectingId(null); setRejectReason(""); };
  const confirmReject = (row) => {
    patch.mutate({ id: row.suggestion_id, status: "rejected", rejection_reason: rejectReason.trim() || null });
  };

  const fmtAgo = (iso) => {
    try {
      const diff = Date.now() - new Date(iso).getTime();
      const mins = Math.floor(diff / 6e4);
      if (mins < 60) return `${mins}m ago`;
      const h = Math.floor(mins / 60);
      if (h < 24) return `${h}h ago`;
      const d = Math.floor(h / 24);
      return `${d}d ago`;
    } catch { return ""; }
  };

  const statusStyle = {
    unread: "bg-brand-accent/15 text-brand-accent border-brand-accent/30",
    read: "bg-brand-sand text-brand-subtle border-brand-border",
    accepted: "bg-brand-primary/15 text-brand-primary border-brand-primary/30",
    archived: "bg-brand-text/10 text-brand-text/60 border-brand-text/20",
    rejected: "bg-brand-error/15 text-brand-error border-brand-error/30",
  };

  const items = q.data?.items || [];
  const hiddenTotal = q.data?.hidden_counts
    ? (q.data.hidden_counts.archived || 0) + (q.data.hidden_counts.rejected || 0)
    : 0;

  return (
    <div className="td-card p-5 sm:p-6" data-testid="suggestions-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Lightbulb size={16} className="text-brand-accent" />
        <h2 className="font-heading text-lg font-medium">Walker suggestions</h2>
        {q.data && (
          <span className="ml-auto text-xs text-brand-subtle" data-testid="suggestions-unread">
            {q.data.unread_count} unread · {q.data.count} shown
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
        <label className="inline-flex items-center gap-2 text-xs text-brand-subtle cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="suggestions-show-hidden"
            checked={includeHidden}
            onChange={(e) => setIncludeHidden(e.target.checked)}
            className="accent-brand-primary"
          />
          Show archived / rejected
        </label>
        {q.data && (
          <span className="text-[11px] text-brand-subtle">
            Hidden: {q.data.hidden_counts?.archived || 0} archived · {q.data.hidden_counts?.rejected || 0} rejected
            {!includeHidden && hiddenTotal > 0 && " (not shown)"}
          </span>
        )}
      </div>

      {q.isLoading && (
        <div className="text-sm text-brand-subtle mt-3 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}

      {q.data && items.length === 0 && (
        <p className="text-sm text-brand-subtle mt-3">
          {includeHidden
            ? "No suggestions in the database."
            : "No active suggestions — nudge users from the About page or toggle 'Show archived / rejected'."}
        </p>
      )}

      {items.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="suggestions-list">
          {items.map((row) => {
            const status = row.status || "unread";
            const isRead = status !== "unread";
            const isAccepted = status === "accepted";
            const isArchived = status === "archived";
            const isRejected = status === "rejected";
            const isTerminal = isAccepted || isArchived || isRejected;
            const rejectingThis = rejectingId === row.suggestion_id;
            return (
              <div
                key={row.suggestion_id}
                data-testid={`suggestion-${row.suggestion_id}`}
                className={`rounded-2xl border p-3 sm:p-4 transition-colors ${
                  isRead ? "bg-brand-sand/50 border-brand-border/60" : "bg-brand-surface border-brand-primary/30"
                } ${isArchived ? "opacity-70" : ""}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusStyle[status] || statusStyle.unread}`}
                      data-testid={`suggestion-status-${row.suggestion_id}`}
                    >
                      {status}
                    </span>
                    {row.page_context && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-brand-sand text-brand-subtle border border-brand-border"
                        title="Screen the user was on when they submitted"
                      >
                        <MapPin size={10} /> {row.page_context}
                      </span>
                    )}
                    <span className="text-[11px] text-brand-subtle">{fmtAgo(row.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!isRead && (
                      <button
                        data-testid={`suggestion-mark-read-${row.suggestion_id}`}
                        onClick={() => patch.mutate({ id: row.suggestion_id, status: "read" })}
                        disabled={patch.isPending}
                        className="td-chip cursor-pointer inline-flex items-center gap-1 !text-[11px]"
                        title="Mark as read"
                      >
                        <Check size={11} /> Read
                      </button>
                    )}
                    {!isAccepted && !isRejected && (
                      <button
                        data-testid={`suggestion-accept-${row.suggestion_id}`}
                        onClick={() => onAccept(row)}
                        disabled={patch.isPending}
                        className="td-chip cursor-pointer inline-flex items-center gap-1 !text-[11px] bg-brand-primary/10 !text-brand-primary !border-brand-primary/40 hover:!bg-brand-primary hover:!text-white"
                        title={row.user_contact ? `Accept + email ${row.user_contact}` : "Accept (no contact to notify)"}
                      >
                        <ThumbsUp size={11} /> Accept
                      </button>
                    )}
                    {!isTerminal && (
                      <button
                        data-testid={`suggestion-reject-${row.suggestion_id}`}
                        onClick={() => startReject(row)}
                        disabled={patch.isPending}
                        className="td-chip cursor-pointer inline-flex items-center gap-1 !text-[11px] !text-brand-error !border-brand-error/40 hover:!bg-brand-error hover:!text-white"
                        title="Reject with an optional reason"
                      >
                        <XCircle size={11} /> Reject
                      </button>
                    )}
                    {!isArchived && !isRejected && (
                      <button
                        data-testid={`suggestion-archive-${row.suggestion_id}`}
                        onClick={() => patch.mutate({ id: row.suggestion_id, status: "archived" })}
                        disabled={patch.isPending}
                        className="td-chip cursor-pointer inline-flex items-center gap-1 !text-[11px] !text-brand-subtle"
                        title="Archive (kept for history, hidden from active queue)"
                      >
                        <Archive size={11} /> Archive
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-brand-text/90 mt-2 leading-relaxed whitespace-pre-wrap">{row.suggestion_text}</p>
                {row.user_contact && (
                  <div className="text-[11px] text-brand-subtle mt-2 flex items-center gap-1">
                    <Mail size={11} /> {row.user_contact}
                  </div>
                )}
                {isRejected && row.rejection_reason && (
                  <div
                    className="text-[11px] text-brand-error/80 mt-2 italic"
                    data-testid={`suggestion-rejection-reason-${row.suggestion_id}`}
                  >
                    Reason: {row.rejection_reason}
                  </div>
                )}
                {rejectingThis && (
                  <div
                    className="mt-3 p-3 rounded-xl bg-brand-error/5 border border-brand-error/20 space-y-2"
                    data-testid={`suggestion-reject-form-${row.suggestion_id}`}
                  >
                    <div className="text-xs text-brand-text/80 font-medium">
                      Rejection reason <span className="text-brand-subtle font-normal">(optional)</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {["Out of scope", "Duplicate", "Already planned", "Not a fit"].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setRejectReason(preset)}
                          className="td-chip cursor-pointer !text-[11px]"
                          data-testid={`suggestion-reject-preset-${preset.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value.slice(0, 200))}
                      placeholder="e.g. Out of scope"
                      className="w-full td-input !text-sm"
                      data-testid={`suggestion-reject-input-${row.suggestion_id}`}
                      autoFocus
                    />
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={cancelReject}
                        className="td-chip cursor-pointer !text-[11px]"
                        data-testid={`suggestion-reject-cancel-${row.suggestion_id}`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => confirmReject(row)}
                        disabled={patch.isPending}
                        className="td-chip cursor-pointer !text-[11px] bg-brand-error !text-white !border-brand-error"
                        data-testid={`suggestion-reject-confirm-${row.suggestion_id}`}
                      >
                        Confirm reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



function CommunityPanels() {
  const [mudMin, setMudMin] = useState(3);
  const topQ = useQuery({
    queryKey: ["admin-top-verified"],
    queryFn: async () => (await api.get("/admin/community/top-verified", { params: { limit: 8 } })).data,
  });
  const mudQ = useQuery({
    queryKey: ["admin-muddy", mudMin],
    queryFn: async () => (await api.get("/admin/community/muddy", { params: { min_level: mudMin, max_level: 4, limit: 12 } })).data,
  });

  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="community-panels">
      <div className="td-card p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <PawPrint size={16} className="text-brand-primary" />
          <h2 className="font-heading text-lg font-medium">Community recommended</h2>
          <span className="text-xs text-brand-subtle ml-auto">top Paw-Verified</span>
        </div>
        <div className="mt-3 space-y-1.5">
          {(topQ.data?.items || []).length === 0 ? (
            <p className="text-sm text-brand-subtle">No Paw-Verified trails yet.</p>
          ) : (
            topQ.data.items.map((t, idx) => (
              <Link
                key={t.location_id}
                to={`/location/${t.location_id}`}
                data-testid={`top-verified-${t.location_id}`}
                className="flex items-center justify-between gap-3 py-2 px-3 rounded-xl hover:bg-brand-sand transition-colors"
              >
                <div className="min-w-0 flex items-center gap-2">
                  <span className="text-brand-subtle text-xs w-5">#{idx + 1}</span>
                  <div className="min-w-0">
                    <div className="font-medium text-brand-text truncate">{t.name}</div>
                    <div className="text-xs text-brand-subtle truncate">{t.region}</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-brand-primary/10 text-brand-primary border border-brand-primary/30 shrink-0">
                  <PawPrint size={11} /> {t.verified_walks_count}
                </span>
              </Link>
            ))
          )}
        </div>
      </div>

      <div className="td-card p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <Droplets size={16} className="text-brand-primary" />
          <h2 className="font-heading text-lg font-medium">Mud hotspots</h2>
          <span className="text-xs text-brand-subtle ml-auto">14-day avg</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="mud-filter">
          {MUD_LEVELS.map((lvl) => (
            <button
              key={lvl.value}
              data-testid={`mud-filter-${lvl.value}`}
              onClick={() => setMudMin(lvl.value)}
              className={"td-chip cursor-pointer " + (mudMin === lvl.value ? "td-chip-active" : "")}
            >
              ≥ {lvl.value} {lvl.label}
            </button>
          ))}
        </div>
        <div className="mt-3 space-y-1.5">
          {(mudQ.data?.items || []).length === 0 ? (
            <p className="text-sm text-brand-subtle">No trails at this mud level.</p>
          ) : (
            mudQ.data.items.map((t) => {
              const meta = levelMeta(t.mud_level_avg);
              return (
                <Link
                  key={t.location_id}
                  to={`/location/${t.location_id}`}
                  data-testid={`muddy-${t.location_id}`}
                  className="flex items-center justify-between gap-3 py-2 px-3 rounded-xl hover:bg-brand-sand transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-brand-text truncate">{t.name}</div>
                    <div className="text-xs text-brand-subtle truncate">{t.region} · {t.mud_reports_count} reports</div>
                  </div>
                  {meta && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border shrink-0 ${meta.color} ${meta.text} ${meta.border}`}>
                      <Droplets size={11} /> {t.mud_level_avg?.toFixed(1)} · {meta.label}
                    </span>
                  )}
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function DigestModal({ onClose }) {
  const previewQ = useQuery({
    queryKey: ["admin-digest-preview"],
    queryFn: async () => (await api.get("/admin/digest/preview", { params: { days: 7 } })).data,
  });
  const sendMut = useMutation({
    mutationFn: async ({ dryRun }) =>
      (await api.post("/admin/digest/send", { days: 7 }, { params: { dry_run: dryRun } })).data,
    onSuccess: (data) => {
      if (data.dry_run) {
        // Dry-run: no emails sent, just an audit row. Keep the modal open so
        // the operator can inspect the shape and then decide to actually send.
        toast.success(`Dry-run OK — would have sent to ${data.recipients.length} admin${data.recipients.length === 1 ? "" : "s"}. No emails were dispatched.`);
      } else {
        toast.success(`Digest sent to ${data.recipients.length} admin${data.recipients.length === 1 ? "" : "s"}`);
        onClose();
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't send digest"),
  });

  return (
    <div
      className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="digest-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-brand-surface rounded-3xl w-full max-w-2xl p-0 animate-fade-up max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-brand-border/60">
          <div>
            <div className="td-section-label">Email</div>
            <h3 className="font-heading text-xl text-brand-text mt-0.5 flex items-center gap-2">
              <Mail size={18} className="text-brand-accent" /> Weekly digest
            </h3>
          </div>
          <button onClick={onClose} className="text-brand-subtle" aria-label="Close" data-testid="digest-close">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {previewQ.isLoading && (
            <div className="flex items-center gap-2 text-sm text-brand-subtle"><Loader2 size={14} className="animate-spin" /> Building preview…</div>
          )}
          {previewQ.data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
                <div className="td-card p-3">
                  <div className="td-section-label">Recipients</div>
                  <div className="font-heading text-xl mt-1" data-testid="digest-recipient-count">
                    {previewQ.data.recipients.length}
                  </div>
                </div>
                <div className="td-card p-3">
                  <div className="td-section-label flex items-center gap-1">
                    <Utensils size={10} /> Walker intent
                  </div>
                  <div
                    className="font-heading text-xl mt-1 text-brand-accent"
                    data-testid="digest-lunch-intents"
                  >
                    {previewQ.data.data.lunch_intents}
                  </div>
                </div>
                <div className="td-card p-3">
                  <div className="td-section-label flex items-center gap-1">
                    <Bus size={10} /> Transit
                  </div>
                  <div
                    className="font-heading text-xl mt-1 text-brand-primary"
                    data-testid="digest-transit-plans"
                  >
                    {previewQ.data.data.transit_plans}
                  </div>
                </div>
                <div className="td-card p-3">
                  <div className="td-section-label">📸 Uploads</div>
                  <div
                    className="font-heading text-xl mt-1 text-[#5C3A1E]"
                    data-testid="digest-community-uploads"
                  >
                    {previewQ.data.data.community_uploads}
                  </div>
                </div>
              </div>
              <div className="text-xs text-brand-subtle mb-2">
                Auto-scheduled every <span className="text-brand-text font-medium">Monday at 00:00 UTC</span>.
                Will be sent from <span className="text-brand-text font-medium">{previewQ.data.from}</span> to{" "}
                <span className="text-brand-text font-medium">{previewQ.data.recipients.join(", ") || "—"}</span>
              </div>
              <div
                className="rounded-2xl border border-brand-border overflow-hidden bg-white"
                data-testid="digest-preview"
                dangerouslySetInnerHTML={{ __html: previewQ.data.html }}
              />
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-brand-border/60 flex gap-2 flex-wrap">
          <button onClick={onClose} className="td-btn-secondary flex-1 !py-2.5" data-testid="digest-cancel">Cancel</button>
          <button
            data-testid="digest-dry-run"
            disabled={sendMut.isPending || !previewQ.data || previewQ.data.recipients.length === 0}
            onClick={() => sendMut.mutate({ dryRun: true })}
            title="Log the send in the audit trail without pinging inboxes — useful to verify the Monday cron shape mid-week."
            className="td-btn-secondary flex-1 !py-2.5"
          >
            {sendMut.isPending && sendMut.variables?.dryRun ? <Loader2 size={14} className="animate-spin" /> : <Beaker size={14} />}
            Dry-run
          </button>
          <button
            data-testid="digest-send"
            disabled={sendMut.isPending || !previewQ.data || previewQ.data.recipients.length === 0}
            onClick={() => sendMut.mutate({ dryRun: false })}
            className="td-btn-primary flex-1 !py-2.5"
          >
            {sendMut.isPending && !sendMut.variables?.dryRun ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send digest now
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminTelemetry() {
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const reportRef = useRef(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [exporting, setExporting] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [trafficFilter, setTrafficFilter] = useState("humans");
  const [detailEventId, setDetailEventId] = useState(null);

  const dash = useQuery({
    queryKey: ["admin-telemetry", trafficFilter],
    enabled: !!user && isAdmin(user),
    queryFn: async () =>
      (await api.get("/admin/telemetry", { params: { limit: 100, traffic: trafficFilter } })).data,
  });

  const handleBlockIp = async (ip, reason) => {
    // Pre-flight: look up org/ASN. If this is a shared VPN exit
    // (Cloudflare WARP, iCloud Private Relay, major VPN provider)
    // warn before blocking so we don't take out real users.
    try {
      const r = await api.get(`/admin/ip-lookup/${encodeURIComponent(ip)}`);
      if (r.data?.classification === "consumer_vpn") {
        const org = r.data?.lookup?.org || r.data?.lookup?.isp || "a shared VPN exit";
        const proceed = window.confirm(
          `This IP looks like ${org} — a shared VPN exit. ` +
          `Blocking it may block real users on the same exit node. Block anyway?`
        );
        if (!proceed) return;
      }
    } catch {
      // Lookup is advisory only — if it fails, continue with the block.
    }
    // Immediate ban with undo toast (5s window).
    try {
      await api.post("/admin/block-ip", { ip, note: `flagged: ${reason || "manual"}` });
      qc.invalidateQueries({ queryKey: ["admin-telemetry"] });
      toast.success(`IP ${ip} blocked`, {
        description: reason ? `Reason: ${reason}` : undefined,
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.delete(`/admin/block-ip/${encodeURIComponent(ip)}`);
              qc.invalidateQueries({ queryKey: ["admin-telemetry"] });
              toast(`Unblocked ${ip}`);
            } catch {
              toast.error("Couldn't undo block");
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't block ${ip}`);
    }
  };

  if (loading) return <div className="p-8 text-brand-subtle">Loading…</div>;
  if (!user || !isAdmin(user)) return <Navigate to="/" replace />;

  const exportPdf = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({
        margin: 10,
        filename: `traildog-telemetry-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: "jpeg", quality: 0.92 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#F6EFE0" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      }).from(reportRef.current).save();
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const clearAll = async () => {
    try {
      const r = await api.delete("/admin/telemetry");
      toast.success(`Cleared ${r.data.deleted} events`);
      setConfirmClear(false);
      setClearConfirmText("");
      qc.invalidateQueries({ queryKey: ["admin-telemetry"] });
    } catch {
      toast.error("Couldn't clear telemetry");
    }
  };

  const d = dash.data;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-telemetry-page">
      <header className="mb-5" data-testid="admin-telemetry-header">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="td-section-label">Admin</div>
            <h1 className="font-heading text-4xl font-semibold text-brand-text mt-1 flex items-center gap-3">
              <Activity size={28} className="text-brand-primary" />
              Telemetry
            </h1>
            <p className="text-brand-subtle text-sm mt-1">Privacy-respecting usage events from real walkers.</p>
          </div>
          {/* Refresh collapsed to an icon-only pill, 44px tap target. */}
          <button
            data-testid="telemetry-refresh"
            onClick={() => dash.refetch()}
            aria-label="Refresh"
            title="Refresh dashboard"
            className="shrink-0 w-11 h-11 rounded-full border border-brand-border bg-brand-surface text-brand-subtle hover:text-brand-primary hover:border-brand-primary/40 flex items-center justify-center transition-colors"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Primary action — the only filled brand-green pill. */}
        <div className="mt-5">
          <Link
            to="/admin/trails/new"
            data-testid="telemetry-to-add-trail"
            className="td-btn-primary w-full lg:w-auto !py-2.5 !px-5 !text-sm whitespace-nowrap min-h-[44px]"
          >
            <FileUp size={14} /> Add trail
          </Link>
        </div>

        {/* Manage section — equal-weight outline pills, 2 cols mobile, 5 desktop. */}
        <div className="mt-6" data-testid="admin-header-manage">
          <div className="td-section-label mb-2">Manage</div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Link
              to="/admin/moderation"
              data-testid="telemetry-to-moderation"
              className="td-btn-secondary relative !py-2 !px-3 !text-sm whitespace-nowrap !text-brand-accent !border-brand-accent/40 min-h-[44px] justify-center"
            >
              <ShieldAlert size={14} /> Pending
            </Link>
            <Link
              to="/admin/locations"
              data-testid="telemetry-to-locations"
              className="td-btn-secondary !py-2 !px-3 !text-sm whitespace-nowrap min-h-[44px] justify-center"
            >
              <Database size={14} /> All locations
            </Link>
            <Link
              to="/admin/users"
              data-testid="telemetry-to-users"
              className="td-btn-secondary !py-2 !px-3 !text-sm whitespace-nowrap min-h-[44px] justify-center"
            >
              <Users size={14} /> Users
            </Link>
            <Link
              to="/admin/reports"
              data-testid="telemetry-to-reports"
              className="td-btn-secondary !py-2 !px-3 !text-sm whitespace-nowrap min-h-[44px] justify-center"
            >
              <Flag size={14} /> Reports
            </Link>
            <button
              data-testid="telemetry-digest"
              onClick={() => setDigestOpen(true)}
              className="td-btn-secondary !py-2 !px-3 !text-sm whitespace-nowrap min-h-[44px] justify-center"
            >
              <Mail size={14} /> Weekly digest
            </button>
          </div>
        </div>

        {/* Tools section — Export & Test are secondary, not primary. */}
        <div className="mt-6" data-testid="admin-header-tools">
          <div className="td-section-label mb-2">Tools</div>
          <div className="grid grid-cols-2 lg:flex lg:flex-wrap gap-3">
            <button
              data-testid="telemetry-export-pdf"
              onClick={exportPdf}
              disabled={exporting || !d}
              className="td-btn-secondary !py-2 !px-3 !text-sm whitespace-nowrap min-h-[44px] justify-center"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Export to PDF
            </button>
            <button
              data-testid="telemetry-force-install-test"
              onClick={async () => {
                await track("install_test_event", { source: "admin_debug", at: new Date().toISOString() });
                toast.success("Test install event sent — refresh in a few seconds to confirm it landed");
                setTimeout(() => dash.refetch(), 1200);
              }}
              className="td-btn-secondary !py-2 !px-3 !text-sm whitespace-nowrap !text-brand-accent !border-brand-accent/40 min-h-[44px] justify-center"
              title="Fire a synthetic install_test_event so you can verify the telemetry pipeline end-to-end"
            >
              <Beaker size={14} /> Test install event
            </button>
          </div>
        </div>
      </header>

      {/* New reviews — appears at the TOP of the admin surface so it's
          the first thing admins see on landing. Pulls `unread_count`
          since the last mark-read, plus any pending reviews. */}
      <NewReviewsCard />

      {dash.isLoading && (
        <div className="td-card p-6 flex items-center gap-2 text-brand-subtle text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading dashboard…
        </div>
      )}

      {d && (
        <div ref={reportRef} className="space-y-5 bg-brand-sand p-2">
          <SummaryCards summary={d.summary || []} totalEvents={d.total_events || 0} activeUsers={d.active_users || 0} />
          <TrafficHealthCard health={d.traffic_health} />
          <FreshLivestockPanel />
          <BasecampSignalsTile />
          <ScoutingPipelineTile />
          <SuggestionsPanel />
          <CommunityPanels />
          <AdminRegionHeatMap />
          <Heatmap data={d.region_heat || []} />

          <div className="td-card p-4 flex items-center gap-2 flex-wrap" data-testid="traffic-filter-bar">
            <span className="td-section-label mr-1">Filter events</span>
            {TRAFFIC_FILTERS.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                data-testid={`traffic-filter-${value}`}
                onClick={() => setTrafficFilter(value)}
                className={`td-chip cursor-pointer inline-flex items-center gap-1.5 ${trafficFilter === value ? "td-chip-active" : ""}`}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>

          <ActivityStream />

          <details className="td-card p-5 sm:p-6" data-testid="raw-events-toggle">
            <summary className="cursor-pointer text-sm font-medium text-brand-subtle hover:text-brand-text">
              Show raw events
            </summary>
            <div className="mt-4 -mx-5 sm:-mx-6">
              <EventsTable rows={d.recent || []} onBlockIp={handleBlockIp} onViewEvent={setDetailEventId} />
            </div>
          </details>
          <p className="text-[10px] text-brand-subtle text-center pt-2">
            Generated {fmtDate(d.generated_at)} · TrailDog admin
          </p>

          {/* Duplicate reviews — admin cleanup for repeated-tap submits.
              Lists (user × trail) groups with >1 visible review, lets
              admins keep the oldest + migrate helpful votes and soft-hide
              the rest. Hidden reviews are excluded from lists everywhere;
              the Undo button restores them. */}
          <DuplicateReviewsCard />

          {/* Danger zone — moved out of the primary header so admins can
              only reach destructive actions after scrolling past the data.
              Confirm modal requires the admin to type CLEAR before the
              existing clear-all API call fires. */}
          <section
            data-testid="admin-danger-zone"
            className="mt-10 rounded-3xl border border-brand-error/40 p-5 sm:p-6 bg-brand-error/5"
          >
            <div className="td-section-label !text-brand-error mb-2">Danger zone</div>
            <p className="text-xs text-brand-subtle mb-3">
              Permanently wipes every telemetry event. Analytics and dashboards reset to zero
              and the action can&apos;t be undone.
            </p>
            <button
              data-testid="telemetry-clear"
              onClick={() => setConfirmClear(true)}
              className="td-btn-secondary !py-2 !px-4 !text-sm !text-brand-error !border-brand-error/40 whitespace-nowrap min-h-[44px]"
            >
              <Trash2 size={14} /> Clear all telemetry
            </button>
          </section>
        </div>
      )}

      {digestOpen && <DigestModal onClose={() => setDigestOpen(false)} />}

      {confirmClear && (
        <div className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-center justify-center p-4" onClick={() => { setConfirmClear(false); setClearConfirmText(""); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-brand-surface rounded-3xl w-full max-w-sm p-6 animate-fade-up">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl">Clear all telemetry?</h3>
              <button onClick={() => { setConfirmClear(false); setClearConfirmText(""); }} className="text-brand-subtle" aria-label="Close"><X size={20} /></button>
            </div>
            <p className="text-sm text-brand-subtle mt-2">
              This permanently deletes every captured event. The dashboard will be empty until new events are recorded.
            </p>
            <label className="block mt-4 text-xs text-brand-subtle">
              Type <span className="font-mono font-bold text-brand-error">CLEAR</span> to confirm
            </label>
            <input
              data-testid="telemetry-clear-input"
              type="text"
              value={clearConfirmText}
              onChange={(e) => setClearConfirmText(e.target.value)}
              placeholder="CLEAR"
              autoFocus
              className="mt-1 w-full px-3 py-2 rounded-xl border border-brand-border bg-brand-sand text-brand-text font-mono focus:outline-none focus:border-brand-error"
            />
            <div className="flex gap-2 mt-5">
              <button data-testid="telemetry-clear-cancel" onClick={() => { setConfirmClear(false); setClearConfirmText(""); }} className="td-btn-secondary flex-1 !py-2.5">Cancel</button>
              <button
                data-testid="telemetry-clear-confirm"
                onClick={clearAll}
                disabled={clearConfirmText !== "CLEAR"}
                className="td-btn-primary flex-1 !py-2.5 !bg-brand-error disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Yes, clear all
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-brand-subtle mt-6">
        <Link to="/discover" className="underline">← Back to app</Link>
      </p>

      <EventDetailsModal eventId={detailEventId} onClose={() => setDetailEventId(null)} />
    </div>
  );
}


// Admin: duplicate-reviews cleanup. One (user × trail) group per row.
// "Hide duplicates" keeps the oldest, migrates helpful votes, soft-hides
// the rest. Toast with "Undo" restores them. Hidden reviews are already
// excluded from public lists, counts and averages by the backend.
function DuplicateReviewsCard() {
  const qc = useQueryClient();
  const dupes = useQuery({
    queryKey: ["admin-duplicate-reviews"],
    queryFn: async () => (await api.get("/locations/admin/duplicate-reviews")).data,
    staleTime: 60_000,
  });
  const hidden = useQuery({
    queryKey: ["admin-recently-hidden"],
    queryFn: async () => (await api.get("/locations/admin/reviews/recently-hidden")).data,
    staleTime: 60_000,
  });
  const [busyKey, setBusyKey] = React.useState("");

  const unhideOne = async (reviewId) => {
    try {
      await api.post("/locations/admin/reviews/unhide", { review_ids: [reviewId] });
      toast.success("Restored");
      qc.invalidateQueries({ queryKey: ["admin-recently-hidden"] });
      qc.invalidateQueries({ queryKey: ["admin-duplicate-reviews"] });
    } catch {
      toast.error("Couldn't restore");
    }
  };
  const deletePermanent = async (reviewId) => {
    const typed = window.prompt(
      "This permanently deletes the review AND its helpful votes. Type DELETE to confirm:",
      "",
    );
    if ((typed || "").trim() !== "DELETE") return;
    try {
      await api.delete(`/locations/admin/reviews/${reviewId}`, { data: { confirm: "DELETE" } });
      toast.success("Review deleted");
      qc.invalidateQueries({ queryKey: ["admin-recently-hidden"] });
      qc.invalidateQueries({ queryKey: ["admin-duplicate-reviews"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  const hideGroup = async (group) => {
    const label = `${group.user_name || "a user"} on ${group.location_name}`;
    if (!window.confirm(
      `Hide ${group.count - 1} duplicate review${group.count - 1 === 1 ? "" : "s"} from ${label}?\n` +
      `The oldest review stays visible; helpful votes move onto it. You can undo straight after.`
    )) return;
    const key = `${group.user_id}:${group.location_id}`;
    setBusyKey(key);
    try {
      const res = await api.post("/locations/admin/duplicate-reviews/hide", {
        user_id: group.user_id,
        location_id: group.location_id,
      });
      const hidden = res.data?.hidden_review_ids || [];
      toast.success(`Hidden ${hidden.length} duplicate${hidden.length === 1 ? "" : "s"}`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/locations/admin/reviews/unhide", { review_ids: hidden });
              toast.success("Restored");
              qc.invalidateQueries({ queryKey: ["admin-duplicate-reviews"] });
            } catch {
              toast.error("Couldn't undo");
            }
          },
        },
      });
      qc.invalidateQueries({ queryKey: ["admin-duplicate-reviews"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't hide duplicates");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <section
      data-testid="admin-duplicate-reviews"
      className="td-card mt-6 p-5 sm:p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="td-section-label mb-1">Duplicate reviews</div>
          <p className="text-xs text-brand-subtle">
            Groups where one walker has posted more than one review on the same trail —
            usually a repeated Submit tap. Hiding keeps the oldest review; helpful votes
            move onto it. Hidden reviews are soft-hidden (not deleted) and don&apos;t count
            anywhere.
          </p>
        </div>
        {dupes.data?.total_groups > 0 && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-brand-accent/10 text-brand-accent whitespace-nowrap">
            {dupes.data.total_groups} group{dupes.data.total_groups === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {dupes.isLoading && <div className="text-sm text-brand-subtle">Loading…</div>}
      {!dupes.isLoading && (dupes.data?.groups?.length || 0) === 0 && (
        <div className="text-sm text-brand-subtle">No duplicate reviews — nothing to clean.</div>
      )}
      <div className="space-y-3">
        {(dupes.data?.groups || []).map((g) => {
          const key = `${g.user_id}:${g.location_id}`;
          return (
            <div
              key={key}
              data-testid={`duplicate-group-${key}`}
              className="rounded-2xl border border-brand-border/60 p-3"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm">
                  <span className="font-medium">{g.user_name || "(unknown walker)"}</span>
                  <span className="text-brand-subtle"> on </span>
                  <span className="font-medium">{g.location_name}</span>
                  <span className="text-brand-subtle"> — {g.count} reviews</span>
                </div>
                <button
                  data-testid={`hide-duplicates-${key}`}
                  disabled={busyKey === key}
                  onClick={() => hideGroup(g)}
                  className="td-btn-secondary !py-1.5 !px-3 !text-xs disabled:opacity-60"
                >
                  {busyKey === key ? "Hiding…" : "Hide duplicates"}
                </button>
              </div>
              <ul className="mt-2 space-y-1 text-xs">
                {(g.reviews || []).map((r, i) => (
                  <li key={r.review_id} className="flex items-start gap-2">
                    <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${i === 0 ? "bg-brand-success/10 text-brand-success" : "bg-brand-border/40 text-brand-subtle"}`}>
                      {i === 0 ? "KEEP" : "HIDE"}
                    </span>
                    <span className="text-brand-subtle">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    <span className="flex-1 line-clamp-2">{r.comment || <em className="text-brand-subtle">(no text)</em>}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      {/* Recently hidden — Undo button restores the soft-hidden review. */}
      {(hidden.data?.items?.length || 0) > 0 && (
        <div className="mt-4 pt-4 border-t border-brand-border/50">
          <div className="td-section-label mb-2">Recently hidden</div>
          <ul className="space-y-2">
            {(hidden.data.items || []).slice(0, 10).map((r) => {
              const reasonLabel = (HIDE_REASONS.find((x) => x.key === r.hidden_reason) || {}).label || "No reason given";
              return (
              <li
                key={r.review_id}
                data-testid={`hidden-review-${r.review_id}`}
                className="flex items-start justify-between gap-3 text-xs rounded-xl border border-brand-border/40 p-2"
              >
                <div className="min-w-0">
                  <div className="text-brand-subtle">
                    {(r.hidden_at || r.created_at || "").slice(0, 16).replace("T", " ")}
                    {" · "}
                    <span className="font-medium">{r.user_name || "(walker)"}</span>
                    {" @ "}
                    <span className="font-medium">{r.location_name}</span>
                  </div>
                  <div className="mt-0.5">
                    <span className="font-medium text-brand-error">Reason:</span>{" "}
                    <span className="text-brand-subtle">{reasonLabel}</span>
                    {r.hidden_note ? <span className="text-brand-subtle"> — {r.hidden_note}</span> : null}
                  </div>
                  <div className="line-clamp-2 mt-0.5">{r.comment || <em className="text-brand-subtle">(no text)</em>}</div>
                </div>
                <div className="flex flex-col gap-1 items-stretch">
                  <button
                    data-testid={`unhide-${r.review_id}`}
                    onClick={() => unhideOne(r.review_id)}
                    className="td-btn-secondary !py-1 !px-2.5 !text-xs whitespace-nowrap"
                  >
                    Unhide
                  </button>
                  <button
                    data-testid={`delete-permanent-${r.review_id}`}
                    onClick={() => deletePermanent(r.review_id)}
                    className="td-btn-secondary !py-1 !px-2.5 !text-xs !text-brand-error !border-brand-error/40 whitespace-nowrap"
                  >
                    Delete permanently
                  </button>
                </div>
              </li>
            );})}
          </ul>
        </div>
      )}
    </section>
  );
}


// New reviews — card at the top of the admin page.
function NewReviewsCard() {
  const qc = useQueryClient();
  const feed = useQuery({
    queryKey: ["admin-reviews-recent"],
    queryFn: async () => (await api.get("/locations/admin/reviews/recent")).data,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const emailPref = useQuery({
    queryKey: ["admin-review-emails-setting"],
    queryFn: async () => (await api.get("/locations/admin/settings/review-emails")).data,
  });
  const [hideTarget, setHideTarget] = React.useState(null);

  const unread = feed.data?.unread_count || 0;
  const items = feed.data?.items || [];

  const markRead = async () => {
    try {
      await api.post("/locations/admin/reviews/mark-read");
      qc.invalidateQueries({ queryKey: ["admin-reviews-recent"] });
      toast.success("Marked read");
    } catch {
      toast.error("Couldn't mark read");
    }
  };
  const toggleEmailPref = async (on) => {
    try {
      await api.post("/locations/admin/settings/review-emails", { admin_review_emails: on });
      qc.invalidateQueries({ queryKey: ["admin-review-emails-setting"] });
      toast.success(on ? "Admin review emails on" : "Admin review emails off");
    } catch {
      toast.error("Couldn't save");
    }
  };
  const decidePending = async (reviewId, action) => {
    try {
      await api.post(`/locations/admin/reviews/${reviewId}/pending-decide`, { action });
      qc.invalidateQueries({ queryKey: ["admin-reviews-recent"] });
      toast.success(action === "approve" ? "Review approved" : "Review removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update review");
    }
  };
  const toggleOzzy = async (row) => {
    try {
      await api.post(
        `/locations/${row.location_id}/reviews/${row.review_id}/ozzy-approve`,
        { approved: !row.admin_approved },
      );
      qc.invalidateQueries({ queryKey: ["admin-reviews-recent"] });
      qc.invalidateQueries({ queryKey: ["loc-reviews", row.location_id] });
      toast.success(!row.admin_approved ? "Ozzy approved" : "Approval cleared");
    } catch {
      toast.error("Couldn't update approval");
    }
  };

  const emailsOn = emailPref.data?.admin_review_emails !== false;
  return (
    <section data-testid="admin-new-reviews" className="td-card p-5 sm:p-6 mb-5">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="td-section-label">New reviews</div>
          {unread > 0 && (
            <span
              data-testid="admin-unread-badge"
              className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand-accent text-white"
            >
              {unread}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-brand-subtle cursor-pointer">
            <input
              type="checkbox"
              data-testid="admin-review-emails-toggle"
              checked={emailsOn}
              onChange={(e) => toggleEmailPref(e.target.checked)}
              className="accent-brand-primary"
            />
            Email me on new reviews
          </label>
          <button
            data-testid="admin-reviews-mark-read"
            onClick={markRead}
            disabled={unread === 0}
            className="td-btn-secondary !py-1.5 !px-3 !text-xs disabled:opacity-50"
          >
            Mark all read
          </button>
        </div>
      </div>
      {feed.isLoading && <div className="text-sm text-brand-subtle">Loading…</div>}
      {!feed.isLoading && items.length === 0 && (
        <div className="text-sm text-brand-subtle">No new reviews — all caught up.</div>
      )}
      <ul className="space-y-3">
        {items.map((r) => {
          const first = (r.user_name || "").split(" ")[0] || "A walker";
          const stars = `🐾 ${r.dog_friendliness}${r.scenery != null ? ` · 🌄 ${r.scenery}` : ""}${r.facilities != null ? ` · 🚰 ${r.facilities}` : ""}`;
          return (
            <li
              key={r.review_id}
              data-testid={`admin-new-review-${r.review_id}`}
              className="rounded-2xl border border-brand-border/60 p-3"
            >
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <Link to={`/location/${r.location_id}`} className="font-medium text-brand-primary underline underline-offset-2">
                  {r.location_name}
                </Link>
                <span className="text-brand-subtle">·</span>
                <span className="text-brand-subtle">{first}</span>
                <span className="text-brand-subtle">·</span>
                <span className="text-xs text-brand-subtle">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                {r.pending && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">PENDING</span>
                )}
                {r.admin_approved && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">🐾 OZZY</span>
                )}
              </div>
              <div className="text-xs text-brand-subtle mt-1">{stars}</div>
              {r.comment && <div className="text-sm mt-1 line-clamp-3">{r.comment}</div>}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <button
                  data-testid={`admin-ozzy-toggle-${r.review_id}`}
                  onClick={() => toggleOzzy(r)}
                  className={`td-btn-secondary !py-1 !px-2.5 !text-xs ${r.admin_approved ? "!text-yellow-700 !border-yellow-500/60" : ""}`}
                  title={r.admin_approved ? "Clear Ozzy approval" : "Mark as Ozzy approved"}
                >
                  🐾 {r.admin_approved ? "Approved" : "Approve"}
                </button>
                <button
                  data-testid={`admin-hide-review-${r.review_id}`}
                  onClick={() => setHideTarget(r)}
                  className="td-btn-secondary !py-1 !px-2.5 !text-xs !text-brand-error !border-brand-error/40"
                  title="Hide this review"
                >
                  Hide
                </button>
                {r.pending && (
                  <>
                    <button
                      data-testid={`admin-pending-approve-${r.review_id}`}
                      onClick={() => decidePending(r.review_id, "approve")}
                      className="td-btn-secondary !py-1 !px-2.5 !text-xs"
                    >
                      Approve
                    </button>
                    <button
                      data-testid={`admin-pending-remove-${r.review_id}`}
                      onClick={() => decidePending(r.review_id, "remove")}
                      className="td-btn-secondary !py-1 !px-2.5 !text-xs !text-brand-error !border-brand-error/40"
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <HideReviewDialog
        open={!!hideTarget}
        review={hideTarget || {}}
        locationId={hideTarget?.location_id}
        onClose={() => setHideTarget(null)}
        onHidden={() => {
          qc.invalidateQueries({ queryKey: ["admin-reviews-recent"] });
          qc.invalidateQueries({ queryKey: ["admin-recently-hidden"] });
        }}
      />
    </section>
  );
}

